/**
 * Notes et évaluations de l'élève (migration 0321) — lecture / écriture en
 * direct sous RLS (l'élève seul). Tables postérieures aux types générés →
 * client générique.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import {
  EVALUATION_MAX_CARDS,
  normalizeEvaluation,
  type EvaluationCardRef,
  type StudentEvaluation,
} from './grades';

const db = supabase as unknown as SupabaseClient;

export const myEvaluationsKey = (userId: string) => ['student-evaluations', userId] as const;

export function useMyEvaluations(userId: string | null | undefined) {
  return useQuery({
    queryKey: myEvaluationsKey(userId ?? ''),
    enabled: !!userId,
    staleTime: 30_000,
    queryFn: async (): Promise<StudentEvaluation[]> => {
      const { data, error } = await db
        .from('student_evaluations')
        .select(
          'id, title, subject, eval_date, score, max_score, coefficient, note, graded_at, student_evaluation_cards(card_id, card_title)',
        )
        .eq('student_id', userId)
        .order('eval_date', { ascending: true });
      if (error) throw new Error(error.message);
      return ((data ?? []) as Record<string, unknown>[]).map(normalizeEvaluation);
    },
  });
}

export interface EvaluationInput {
  id?: string;
  title: string;
  subject: string | null;
  eval_date: string;
  score: number | null;
  max_score: number;
  coefficient: number;
  note: string | null;
  cards: EvaluationCardRef[];
}

/** Crée ou modifie une éval, puis remplace sa liste de SKILLCARDS. */
export function useSaveEvaluation(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: EvaluationInput): Promise<string> => {
      const row = {
        title: input.title.trim(),
        subject: input.subject?.trim() || null,
        eval_date: input.eval_date,
        score: input.score,
        max_score: input.max_score,
        coefficient: input.coefficient,
        note: input.note?.trim() || null,
      };
      let id = input.id;
      if (id) {
        const { error } = await db.from('student_evaluations').update(row).eq('id', id);
        if (error) throw new Error(error.message);
        const { error: delErr } = await db
          .from('student_evaluation_cards')
          .delete()
          .eq('evaluation_id', id);
        if (delErr) throw new Error(delErr.message);
      } else {
        const { data, error } = await db
          .from('student_evaluations')
          .insert(row)
          .select('id')
          .single();
        if (error) throw new Error(error.message);
        id = (data as { id: string }).id;
      }
      const cards = input.cards.slice(0, EVALUATION_MAX_CARDS);
      if (cards.length > 0) {
        const { error } = await db.from('student_evaluation_cards').insert(
          cards.map((c) => ({
            evaluation_id: id,
            card_id: c.card_id,
            card_title: c.card_title.slice(0, 200) || 'SKILLCARD',
          })),
        );
        if (error) throw new Error(error.message);
      }
      return id!;
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: myEvaluationsKey(userId) }),
  });
}

/** Saisit (ou corrige) seulement la note d'une éval. */
export function useGradeEvaluation(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      score: number | null;
      max_score: number;
      coefficient: number;
    }) => {
      const { error } = await db
        .from('student_evaluations')
        .update({
          score: input.score,
          max_score: input.max_score,
          coefficient: input.coefficient,
        })
        .eq('id', input.id);
      if (error) throw new Error(error.message);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: myEvaluationsKey(userId) }),
  });
}

export function useDeleteEvaluation(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      // Photos de l'éval (0333) : les objets Storage d'abord (la cascade SQL
      // ne supprime que les lignes).
      const { data: photos } = await db
        .from('student_evaluation_photos')
        .select('storage_path')
        .eq('evaluation_id', id);
      const paths = ((photos ?? []) as { storage_path: string }[]).map((p) => p.storage_path);
      if (paths.length > 0) await db.storage.from('evaluation-photos').remove(paths);
      const { error } = await db.from('student_evaluations').delete().eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: myEvaluationsKey(userId) }),
  });
}

/** Message lisible d'une erreur d'enregistrement. */
export function evaluationErrorMessage(message: string): string {
  if (message.includes('student_evaluations_score_max_chk')) {
    return 'La note ne peut pas dépasser le barème.';
  }
  if (message.includes('check')) return 'Une valeur n’est pas valide. Vérifie le formulaire.';
  return 'L’enregistrement a échoué. Réessaie.';
}
